/**
 * Contact-state pill-variant vocabulary — the single source of truth for
 * pill/fact-card labels and the fact→label derivation logic every consumer
 * of the contact-state resolver (plans/2026-07-22-001-contact-state-resolver-
 * plan.md) needs to agree on.
 *
 * bd-ozirv.7 (2026-09-02 thermonuclear audit, flow-engagement-plays:1) found
 * this vocabulary triplicated: each server adapter
 * (src/services/flow/contact-state-sources.ts) exported its own copy of the
 * label constants; `PILL_DIMENSION_PRIORITY`
 * (src/shared/schemas/flow-contact-state.ts) carried its own literal-string
 * copies of the same labels; and the frontend
 * (web/app-home/src/components/flow/contact/StatePills.tsx) re-derived the
 * SAME fact→label branches via four hand-written predicates + narrow Zod
 * schemas, each flagged "kept in sync BY HAND, not by import" because a
 * frontend bundle cannot import `src/services/*` server code (CLAUDE.md).
 * This file lives under `src/shared/` for exactly that reason — both the
 * server adapters and the frontend bundle can import it.
 *
 * `pillLabelForFact` is exhaustive over `StateDimension` (see the
 * `satisfies`-style switch below) — adding a ninth dimension without adding
 * a branch here is a compile error, not a silent gap.
 */

import { z } from 'zod';
import type { StateFact } from '../schemas/flow-contact-state';
import {
  RecentInviteDetailSchema,
  SignalActionDetailSchema,
  WarmIntroDetailSchema,
  RecentRequestDetailSchema,
} from '../schemas/flow-contact-state-details';

// Dimension-level fallback labels — one label per dimension with no
// per-fact variant (in_flow, queued, gifted), plus the DEFAULT variant for
// a dimension that does have one (invited → Recent Campaign, open_task →
// Live Task). Every adapter's `ContactStateSource.label` field and the
// frontend's `DIMENSION_STYLES` default now read from here instead of each
// carrying its own literal.

/** `in_flow` (gongFlowSource) — no per-fact variant. */
export const GONG_FLOW_LABEL = 'Gong Flow';
/**
 * Suffix on an In Flow pill / enroll warning answered from the synced
 * `salesforce_contact.gong_in_flow` flag (no Gong probe row for the contact).
 * The flag lags Gong, so the copy says so (U27, D14).
 */
export const GONG_SF_FALLBACK_NOTE = '(per Salesforce, may lag)';
/** `queued` (enrollmentQueueSource) — no per-fact variant. */
export const QUEUED_LABEL = 'Queued';
/** `gifted` — no adapter yet (StateDimensionSchema doc comment); reserved default. */
export const GIFT_LABEL = 'Gift';

/**
 * `invited` variants (launcherInvitesSource, Fix A / Spencer 2026-07-31):
 * a campaign-membership fact (tiers 1/2) reads 'Upcoming Campaign' when its
 * campaign's start_date is in the future, else 'Recent Campaign'; a
 * suggested-tier fact (tier 3) always reads 'Suggested Invite'.
 */
export const RECENT_CAMPAIGN_LABEL = 'Recent Campaign';
export const UPCOMING_CAMPAIGN_LABEL = 'Upcoming Campaign';
export const SUGGESTED_INVITE_LABEL = 'Suggested Invite';

/**
 * `open_task` variants (signalActionsSource, ranking refinement 2026-07-27):
 * closed wins first regardless of linkage ('Recent Task'); among open
 * tasks, gong-linked splits to 'Gong Task', everything else is 'Live Task'.
 */
export const LIVE_TASK_LABEL = 'Live Task';
export const GONG_TASK_LABEL = 'Gong Task';
export const RECENT_TASK_LABEL = 'Recent Task';

/**
 * `recent_request` (recentRequestSource, flow-workflow-requests U8): a fact
 * whose detail carries a staged `contact_field_overlay` value gets a label
 * that EMBEDS the value itself; otherwise the fixed dimension default.
 */
export const RECENT_REQUEST_LABEL = 'Recent Request';

/**
 * `warm_intro` (warmIntroSource, warm-intro plan R3): the label IS the
 * connector name, prefixed — `${WARM_INTRO_LABEL_PREFIX}${connectorName}`.
 * `WARM_INTRO_LABEL` is a dimension-level fallback only — a real warm-intro
 * fact always has a connector name (the adapter drops a nameless row before
 * it can produce one), so this text never appears on a real pill.
 */
export const WARM_INTRO_LABEL_PREFIX = 'Warm Intro: ';
export const WARM_INTRO_LABEL = 'Warm Intro';

/** `recent_queue_send` (recentQueueSendSource) — flat, no per-fact variant. */
export const RECENT_QUEUE_SEND_LABEL = 'Recent Queue Send';

/**
 * Renders a staged `contact_field_overlay` value for a `recent_request`
 * pill/fact-card label (moved from contact-state-sources.ts's
 * recentRequestSource.pillLabel — colocated with the rest of the
 * derivation it belongs to). `JSON.stringify` only throws for circular
 * refs / BigInt — an unstringifiable value still needs SOME display text,
 * so a fixed literal (not `String(value)`, which would render
 * "[object Object]" for a plain object) is the honest fallback.
 */
function formatStagedValueForDisplay(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value === null || value === undefined) return '';
  try {
    return JSON.stringify(value);
  } catch {
    return '(unrepresentable value)';
  }
}

/**
 * Narrow shapes `pillLabelForFact` reads off `fact.detail` — one per
 * dimension that carries a variant. Each is derived via `Partial<Pick<z.infer
 * <typeof X>, ...>>` off the SAME schema each adapter's `resolve()` uses to
 * build its `detail` payload (src/shared/schemas/flow-contact-state-details.ts,
 * independent-verdict finding 1, session thermo-seven) — a field rename on
 * either side is now a compile error here, not a silent drift into the
 * fallback label. `Partial` because `detail` is `z.unknown()` on the wire
 * (StateFactSchema) and may be malformed/missing; every read below still
 * uses the SAME defensive `as Partial<...>` cast + optional chaining the
 * original per-adapter closures used (never a throw on a malformed/missing
 * detail) — this is a lift of that existing, already-reviewed idiom into
 * one place, not a redesign of it.
 */
type InviteVariantDetail = Partial<Pick<z.infer<typeof RecentInviteDetailSchema>, 'tier' | 'campaignBucket'>>;
type SignalActionVariantDetail = Partial<
  Pick<z.infer<typeof SignalActionDetailSchema>, 'closedDate' | 'isGongFlowTask'>
>;
type WarmIntroVariantDetail = Partial<Pick<z.infer<typeof WarmIntroDetailSchema>, 'connectorName'>>;
type RecentRequestVariantDetail = Partial<Pick<z.infer<typeof RecentRequestDetailSchema>, 'stagedValue'>>;

/** The minimal shape `pillLabelForFact` needs — a real `StateFact` satisfies this. */
export type PillVariantFact = Pick<StateFact, 'dimension' | 'detail'>;

/**
 * The one fact→label derivation, exhaustive over `StateDimension`. Used
 * directly as `ContactStateSource.pillLabel` by every adapter that has a
 * per-fact variant (launcherInvitesSource, signalActionsSource,
 * warmIntroSource, recentRequestSource — the "four adapters" bd-ozirv.7
 * names), and by the frontend (StatePills.tsx) to derive a StateFactCard's
 * label/style without a wire `pill.label` to read (a StateFact carries no
 * `label` field — only pills do).
 */
export function pillLabelForFact(fact: PillVariantFact): string {
  switch (fact.dimension) {
    case 'in_flow':
      return GONG_FLOW_LABEL;
    case 'invited': {
      const detail = fact.detail as InviteVariantDetail | undefined;
      if (detail?.tier === 'suggested') return SUGGESTED_INVITE_LABEL;
      return detail?.campaignBucket === 'upcoming' ? UPCOMING_CAMPAIGN_LABEL : RECENT_CAMPAIGN_LABEL;
    }
    case 'queued':
      return QUEUED_LABEL;
    case 'open_task': {
      const detail = fact.detail as SignalActionVariantDetail | undefined;
      if (detail?.closedDate) return RECENT_TASK_LABEL;
      if (detail?.isGongFlowTask) return GONG_TASK_LABEL;
      return LIVE_TASK_LABEL;
    }
    case 'gifted':
      return GIFT_LABEL;
    case 'recent_request': {
      const detail = fact.detail as RecentRequestVariantDetail | undefined;
      if (detail?.stagedValue === undefined || detail.stagedValue === null) return RECENT_REQUEST_LABEL;
      const displayValue = formatStagedValueForDisplay(detail.stagedValue);
      return displayValue || RECENT_REQUEST_LABEL;
    }
    case 'warm_intro': {
      const detail = fact.detail as WarmIntroVariantDetail | undefined;
      return detail?.connectorName ? `${WARM_INTRO_LABEL_PREFIX}${detail.connectorName}` : WARM_INTRO_LABEL;
    }
    case 'recent_queue_send':
      return RECENT_QUEUE_SEND_LABEL;
  }
}

/**
 * True for an `open_task` fact whose signal action IS a Gong Engage flow's
 * own work item (pill-dedup-priority, Spencer 2026-07-27), regardless of
 * open/closed — used by the frontend's "· Gong flow" annotation, which is
 * independent of (and narrower than) the closed/open label split above: a
 * CLOSED gong-linked task still reads `RECENT_TASK_LABEL` (closed wins
 * first), so the annotation needs this separate signal to know linkage.
 */
export function isGongLinkedOpenTaskFact(fact: PillVariantFact): boolean {
  if (fact.dimension !== 'open_task') return false;
  const detail = fact.detail as SignalActionVariantDetail | undefined;
  return !!detail?.isGongFlowTask;
}
